import React from "react";
import { useLang } from "../i18n/LanguageContext";
import { useContent } from "../lib/useContent";
import { useSeo, Reveal, Icon, LazyImg } from "../components/common";
import { PageHero } from "../components/PageHero";
import { SectionHead, CTABand } from "../components/blocks";
import { fileUrl } from "../lib/api";
import { WHY_CHOOSE, IMG } from "../data/site";

export default function About() {
  const { t, lang } = useLang();
  useSeo("About Sparko | Premium Cleaning Company in the Netherlands", "Learn about Sparko — a premium, eco-friendly cleaning and maintenance company serving businesses and homes across the Netherlands.");
  const { data: team } = useContent("team");

  const values = [
    { icon: "Gem", title: "Premium Standards", text: "We treat every space as if it were our own, delivering a consistently flawless finish." },
    { icon: "Leaf", title: "Sustainability First", text: "Certified green products and efficient methods that protect people and the planet." },
    { icon: "Handshake", title: "Trusted Partnership", text: "Reliable, insured and transparent — we build long-term relationships, not transactions." },
  ];

  return (
    <div>
      <PageHero title="About Sparko" crumb={t.nav.about} bg={IMG.office3}
        sub="A premium Dutch cleaning company built on precision, sustainability and genuine care." />

      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24 grid md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative">
            <LazyImg src={IMG.founder} alt="Sparko specialist operating a professional floor-polishing machine" className="rounded-3xl w-full aspect-[4/5] object-cover shadow-xl" />
          </div>
        </Reveal>
        <div>
          <SectionHead eyebrow="Our Story" title="Redefining clean across the Netherlands" />
          <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
            <p>Sparko was founded on a simple belief: that a truly clean environment transforms how people feel, work and live. From bustling Amsterdam offices to boutique Rotterdam hotels, our certified specialists deliver a premium standard of cleanliness that speaks for itself.</p>
            <p>We combine experienced teams, professional-grade equipment and certified eco-friendly products to guarantee results — every visit, without compromise. Fully insured and always reliable, Sparko is the partner Dutch businesses trust.</p>
          </div>
        </div>
      </section>

      <section className="bg-muted py-24" style={{ background: "var(--muted)" }}>
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHead center eyebrow="What Drives Us" title="Our Core Values" />
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl bg-white p-8 border border-black/5 hover-lift">
                  <div className="grid place-items-center h-14 w-14 rounded-2xl bg-blue/10 text-blue"><Icon name={v.icon} size={24} /></div>
                  <h3 className="mt-6 font-display text-xl text-navy">{v.title}</h3>
                  <p className="mt-3 text-slate-500 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-8 py-24">
        <SectionHead center eyebrow="Why Sparko" title={t.sections.whyTitle} />
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {WHY_CHOOSE.map((w, i) => (
            <Reveal key={w.en} delay={(i % 4) * 0.06}>
              <div className="group h-full rounded-3xl bg-white p-6 border border-black/5 hover-lift">
                <div className="grid place-items-center h-12 w-12 rounded-2xl bg-blue/10 text-blue group-hover:bg-blue group-hover:text-white transition-colors"><Icon name={w.icon} size={22} /></div>
                <h3 className="mt-5 font-display text-lg text-navy">{w[lang]}</h3>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {team.length > 0 && (
        <section className="bg-muted py-24" style={{ background: "var(--muted)" }}>
          <div className="mx-auto max-w-7xl px-5 md:px-8">
            <SectionHead center eyebrow="Our People" title="Meet the Team" />
            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-6">
              {team.map((m, i) => (
                <Reveal key={m.id} delay={i * 0.08}>
                  <div className="rounded-3xl bg-white p-8 text-center border border-black/5 hover-lift">
                    <div className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-navy text-white font-display text-2xl">
                      {m.image ? <img src={fileUrl(m.image)} alt={m.name} className="h-full w-full rounded-full object-cover" /> : m.name.charAt(0)}
                    </div>
                    <h3 className="mt-5 font-display text-lg text-navy">{m.name}</h3>
                    <p className="text-sm text-blue">{m.role}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </div>
  );
}
